/* eslint-disable max-len */

import { LoginByVinErrorPageVwView } from '../../pages/login-by-vin-error-page/LoginByVinErrorPageVwView';
import { LoginByVinPageVwView } from '../../pages/login-by-vin-page/LoginByVinPageVwView';
import { IViewProvider } from '../contexts/view-provider-context/IViewProvider';
import { LoginPageVwView } from '../../pages/login-page/LoginPageVwView';
import { LoginErrorPageVwView } from '../../pages/login-error-page/LoginErrorPageVwView';
import { BookingsPageVwView } from '../../pages/bookings-page/BookingsPageVwView';
import { ErrorPageVwView } from '../../pages/error-page/ErrorPageVwView';
import { PageContentLayoutVwView } from './page-content-layout/PageContentLayoutVwView';
import { ErrorFallbackVwView } from '../error-boundaries/ErrorFallbaclVwView';
import { ApiErrorModalContentVwView } from '../api/api-error-modal-content/ApiErrorModalContentVwView';
import { CancelConfirmationModalContentVwView } from '../../pages/bookings-page/cancel-confirmation-modal-content/CancelConfirmationModalContentVwView';
import { LoaderVwView } from '../loader/LoaderVwView';
import { WizardVwView } from '../wizard/WizardVwView';
import { StepsNavigationView } from '../wizard/StepsNavigation/StepsNavigationView';
import { VehicleDetailsPageVwView } from '../../pages/vehicle-details-page/VehicleDetailsPageVwView';
import { ServiceRequestPageVwView } from '../../pages/service-request-page/ServiceRequestPageVwView';
import { SelectDealerPageVwView } from '../../pages/select-dealer-page/SelectDealerPageVwView';
import { AgreementWithServicePlanModalContentVwView } from '../../pages/price-estimate-with-service-plan-page/agreement-with-service-plan-modal-content/AgreementWithServicePlanModalContentVwView';
import { PriceEstimateWithServicePlanPageVwView } from '../../pages/price-estimate-with-service-plan-page/PriceEstimateWithServicePlanPageVwView';
import { PriceEstimateWithoutServicePlanPageVwView } from '../../pages/price-estimate-without-service-plan-page/PriceEstimateWithoutServicePlanPageVwView';
import { AgreementWithoutServicePlanModalContentVwView } from '../../pages/price-estimate-without-service-plan-page/agreement-without-service-plan-modal-content/AgreementWithoutServicePlanModalContentVwView';
import { SelectServiceDatePageVwView } from '../../pages/select-service-date-page/SelectServiceDatePageVwView';
import { SelectCollectionDeliveryPageVwView } from '../../pages/select-collection-delivery/SelectCollectionDeliveryPageVwView';
import { CustomerContactPageVwView } from '../../pages/customer-contact-page/CustomerContactPageVwView';
import { PrivacyStatementModalContentVwView } from '../../pages/customer-contact-page/privacy-statement-modal-content/PrivacyStatementModalContentVwView';
import { VerifyBookingDetailsPageVwView } from '../../pages/verify-booking-details-page/VerifyBookingDetailsPageVwView';
import { SendConfirmationMessagePageVwView } from '../../pages/send-confirmation-message-page/SendConfirmationMessagePageVwView';
import styled from 'styled-components';
import {
  ApplicationRoot,
  Breakpoints,
  Container,
  ContainerGutter,
  ContainerPadding,
  CTA,
  FocusLayer,
  IntegratorRoot,
  LayerWrapper,
  Layout,
  ShimLayer,
  ThemeProvider,
} from '@volkswagen-onehub/components-core';
import React, { ReactElement, ReactNode, useState } from 'react';
import { ModalManagerProps } from '../contexts/modal-manager-context/ModalManager';
import { ModalManagerProvider } from '../contexts/modal-manager-context/ModalManagerProvider';
import { useIsFetching, useIsMutating } from 'react-query';
import { ViewProvider } from '../contexts/view-provider-context/ViewProvider';
import { ErrorBoundary } from 'react-error-boundary';
import { reportClientError } from '../error-reporting/ErrorReporter';
import { PageContentLayout } from './page-content-layout/PageContentLayout';
import { Loader } from '../loader/Loader';
import { ModalContentVwView } from "../modal/ModalContentVwView";
import { NoDateSelectedModalContentVwView } from "../modal/NoDateSelectedModalContent/NoDateSelectedModalContentVwView";
import { NoVwVehicleModalContentVwView } from "../modal/NoVwVehicleModalContent/NoVwVehicleModalContentVwView";

const viewProviderValue: IViewProvider = {
  renderLoginPage: LoginPageVwView,
  renderLoginByVinPage: LoginByVinPageVwView,
  renderLoginByVinErrorPage: LoginByVinErrorPageVwView,
  renderLoginErrorPage: LoginErrorPageVwView,
  renderBookingsPage: BookingsPageVwView,
  renderErrorPage: ErrorPageVwView,
  renderPageContentLayout: PageContentLayoutVwView,
  renderErrorFallback: ErrorFallbackVwView,
  renderApiErrorModalContent: ApiErrorModalContentVwView,
  renderModalContent: ModalContentVwView,
  renderNoDateSelectedModalContent: NoDateSelectedModalContentVwView,
  renderNoVwVehicleModalContent: NoVwVehicleModalContentVwView,
  renderCancelConfirmationModalContent: CancelConfirmationModalContentVwView,
  renderLoader: LoaderVwView,
  renderWizard: WizardVwView,
  renderStepsNavigation: StepsNavigationView,
  renderVehicleDetailsPage: VehicleDetailsPageVwView,
  renderServiceRequestPage: ServiceRequestPageVwView,
  renderSelectDealerPage: SelectDealerPageVwView,
  renderAgreementWithServicePlanModalContent: AgreementWithServicePlanModalContentVwView,
  renderPriceEstimateWithServicePlanPage: PriceEstimateWithServicePlanPageVwView,
  renderPriceEstimateWithoutServicePlanPage: PriceEstimateWithoutServicePlanPageVwView,
  renderAgreementWithoutServicePlanModalContent: AgreementWithoutServicePlanModalContentVwView,
  renderSelectServiceDatePage: SelectServiceDatePageVwView,
  renderSelectCollectionDeliveryPage: SelectCollectionDeliveryPageVwView,
  renderCustomerContactPage: CustomerContactPageVwView,
  renderPrivacyStatementModalContent: PrivacyStatementModalContentVwView,
  renderVerifyBookingDetailsPage: VerifyBookingDetailsPageVwView,
  renderSendConfirmationMessagePage: SendConfirmationMessagePageVwView,
};

const StyledModalActionsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: ${(props) => props.theme.size.static350};
  column-gap: ${(props) => props.theme.size.static350};
  padding-top: ${(props) => props.theme.size.static350};

  @media (max-width: ${Breakpoints.b560}px) {
    & > * {
      flex-grow: 1;
      flex-direction: column;
    }
  }
`;

function ModalLayout({ children }: { children?: ReactNode }): ReactElement {
  const [modalProps, setModalProps] = useState<ModalManagerProps>();
  const [modalVisible, setModalVisible] = useState(false);

  const openModal = (props: ModalManagerProps): void => {
    setModalProps(props);
    setModalVisible(true);
  };
  const onCloseModal = (): void => {
    setModalVisible(false);
  };

  return (
    <ModalManagerProvider
      value={{
        open: openModal,
      }}>
      {children}
      <LayerWrapper>
        {modalVisible &&
          modalProps && [
            <ShimLayer key="shim-modal" onClick={onCloseModal} />,
            <FocusLayer
              key="content-layer-v2"
              active={modalVisible}
              size="A"
              ariaLabelledBy="headline-id"
              ariaLabel={modalProps.title ?? ''}>
              <Container gutter={ContainerGutter.static300} padding={ContainerPadding.grid001} wrap="always">
                <Layout
                  appearance={{
                    [Breakpoints.default]: [
                      { name: '.', columns: 2 },
                      { name: 'a', columns: 20 },
                      { name: '.', columns: 2 },
                    ],
                  }}
                  allowOverflowingContent>
                  {modalProps.content()}
                  <StyledModalActionsWrapper>
                    {modalProps.actions &&
                      modalProps.actions.map((action) => (
                        <CTA
                          tag="button"
                          emphasis={action.type === 'text' ? 'tertiary' : action.type}
                          key={action.title}
                          onClick={() => action.onClick(onCloseModal)}>
                          {action.title}
                        </CTA>
                      ))}
                  </StyledModalActionsWrapper>
                </Layout>
              </Container>
            </FocusLayer>,
          ]}
      </LayerWrapper>
    </ModalManagerProvider>
  );
}

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  // <ApplicationRoot>
  & > div,
  & > div > div,
    // <IntegratorRoot>
  & > div > div > div {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
`;

export interface VwLayoutProps {
  children?: ReactNode;
}

// Render errors do not go through react-query, so they are logged separately.
function reportRenderError(error: Error, info: React.ErrorInfo): void {
  reportClientError({
    message: `Render error: ${error.message}`,
    details: `${error.stack || ''}\n${info.componentStack || ''}`,
  });
}

export function VwLayout({ children }: VwLayoutProps): ReactElement {
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();
  return (
    <StyledWrapper>
      <ApplicationRoot>
        <IntegratorRoot>
          <ThemeProvider theme="main">
            <ViewProvider value={viewProviderValue}>
              <ErrorBoundary FallbackComponent={ErrorPageVwView} onError={reportRenderError}>
                <ModalLayout>
                  <PageContentLayout>{children}</PageContentLayout>
                </ModalLayout>
                {!!(isFetching || isMutating) && <Loader />}
              </ErrorBoundary>
            </ViewProvider>
          </ThemeProvider>
        </IntegratorRoot>
      </ApplicationRoot>
    </StyledWrapper>
  );
}
